import { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import dayjs from 'dayjs'
import specializedReportService, { specializedAttachFormats } from '@api/v1/specializedReportService'
import reportJobService from '@api/v1/reportJobService'
import useLatestRun from '@hooks/useLatestRun'
import SpecializedReportShell from '@components/reports/SpecializedReportShell'
import SpecializedFilterBar from '@components/reports/SpecializedFilterBar'
import { objectField, numberField } from '@components/reports/specializedFilterFields'
import ReportViewsMenu from '@components/reports/ReportViewsMenu'
import useSavedReportView from '@components/reports/useSavedReportView'
import { describeFilters } from '@components/reports/reportFilterSummary'
import CsvReportGrid from '@components/reports/CsvReportGrid'

const TYPE_COMPANIES = 3

// Spec 203: the run is legacy's own file (CB's TranslateToReport): one row per client
// with legacy's month columns and its Grand Total. The dispatcher run this page used
// to make failed every time, and answered an empty client with 0 rows when it bound.
const EMPTY = { headers: [], rows: [], meta: [] }

const FIELDS = [
  objectField({ key: 'client', label: 'Client', typeId: TYPE_COMPANIES, idKey: 'clientId', nameKey: 'clientName' }),
  numberField({ key: 'year', label: 'Year', valueKey: 'year', fallback: dayjs().year() }),
]

const makeDefaults = () => ({
  clientId: null,
  clientName: '',
  year: dayjs().year(),
})

// The wire object. `clientFieldId` has NO control and must survive every filter
// edit — it names the lookup field on the bill that joins to the client (the
// CC-49833 / CC-50020 failure mode). Legacy's view model replaces it with the
// tenant's BillableClientLookup before every run (AccountingModels.cs:3440), so
// this value is a floor, not the tenant's answer. Kept module-level so the
// interactive run, the export and a queued run cannot diverge.
const toWire = (f) => ({
  clientId: f.clientId,
  clientName: f.clientName,
  year: f.year,
  clientFieldId: 1010,
})

// Module-level so useLatestRun's `run` keeps one identity (spec 131).
const runErrorMessage = (err) => err.message || 'Report failed'

const BillJournalReport = ({ reportId, name = 'Bill Journal Summary', summary, jobId }) => {
  const [filters, setFilters] = useState(makeDefaults)
  // Spec 131: only the NEWEST run may write the rows, the spinner, the error or
  // the documents' criteria. On QA the split button's "Run report now" started a
  // second run while one was out; that item is now disabled mid-run too, so this
  // gate is the structural guarantee rather than the only one.
  const {
    running, error, ran, runKey, run: startRun,
  } = useLatestRun({ initialRunning: !!jobId, errorMessage: runErrorMessage })
  // CC-50013 (G3): saved views. The hook carries the grid's page state, so
  // the menu cannot be mounted without it (the CC-49732 defect).
  const view = useSavedReportView(setFilters)
  // CC-50029: exports state the period they cover.
  const filtersSummary = useMemo(() => describeFilters(filters.year ? `Year ${filters.year}` : null), [filters])
  const [data, setData] = useState(EMPTY)
  const [savedAsOf, setSavedAsOf] = useState(null)
  // Consume-once: the first run reopens the tray's saved job; later runs are fresh.
  const jobRef = useRef(jobId || null)

  const filterPayload = useMemo(() => toWire(filters), [filters])

  const runReport = useCallback(() => startRun(async () => {
    const jid = jobRef.current
    jobRef.current = null // consumed — subsequent runs are fresh
    if (jid) {
      // Tray "View report" → reopen the exact saved snapshot + show its run time.
      const [st, r] = await Promise.all([reportJobService.status(jid), reportJobService.result(jid)])
      return () => { setSavedAsOf(st?.createdAt || null); setData(r?.data || EMPTY) }
    }
    const result = await specializedReportService.billJournal.run(filterPayload, { reportId })
    // Spec 131 decision 3: the saved-result banner clears at the COMMIT.
    return () => { setSavedAsOf(null); setData(result || EMPTY) }
  }, { criteria: { filtersSummary } }), [filterPayload, reportId, startRun, filtersSummary])

  // Spec 131: deferred and cancellable, so StrictMode's simulated remount starts
  // ONE mount run, not two — the second would otherwise now deterministically
  // supersede a ?jobId= reopen in dev.
  useEffect(() => {
    const t = setTimeout(() => { runReport() }, 0)
    return () => clearTimeout(t)
  }, [])  // eslint-disable-line react-hooks/exhaustive-deps

  // CC-50013 G2 / CC-50148: shared metadata-driven bar (see FIELDS / toWire).
  const filterBar = (
    <SpecializedFilterBar
      fields={FIELDS}
      filters={filters}
      onFiltersChange={setFilters}
      defaults={makeDefaults()}
      onRun={runReport}
      isLoading={running}
      queue={{
        reportId,
        name,
        actionKey: 'bill-journal',
        // Queues the SAME payload the interactive run posts — including
        // clientFieldId.
        endpoint: (f) => ({ method: 'POST', path: '/reports/bill-journal', body: toWire(f) }),
        // Spec 206: its emailed file is its own export, the run's request.
        attachFormats: specializedAttachFormats('/reports/bill-journal'),
      }}
      actions={(
        <ReportViewsMenu
          storageKey="report:bill-journal"
          currentFilters={filters}
          onApply={view.applyView}
          getPageState={view.getPageState}
        />
      )}
    />
  )

  return (
    <SpecializedReportShell
      name={name} summary={summary} running={running} error={error}
      ranCriteria={ran?.criteria ?? null} runKey={runKey}
      filterBar={filterBar} filtersSummary={filtersSummary} savedAsOf={savedAsOf}
      download={(format, getToken) =>
        specializedReportService.billJournal.download(filterPayload, format, getToken, `bill-journal.${format}`)
      }
    >
      <CsvReportGrid key={view.gridKey} data={data} storageKey="report:bill-journal" pageStateRef={view.pageStateRef} initialPageState={view.initialPageState} />
    </SpecializedReportShell>
  )
}

export default BillJournalReport
